/**
 * The ProseMirror schema for the in-place block editor (TASK-050).
 *
 * ==================================================================
 * WHY THIS MIRRORS THE STORED JSON, FIELD BY FIELD
 * ==================================================================
 * Documents are stored as ProseMirror-shaped JSON already -- `paragraph` with
 * `attrs: {id, h, rev, heading}`, text runs carrying `marks: [{type:'bold'}]`.
 * The editor round-trips a block through `Node.fromJSON` and `toJSON`, so
 * anything this schema does NOT declare is silently dropped on the way back
 * out. A missing attr here is a field that disappears from the document the
 * first time somebody fixes a typo in it.
 *
 * So every attribute the stored shape carries is declared, including the ones
 * the editor never touches:
 *   - `id`   the block's identity, which comment anchors point at and which
 *            the op vocabulary targets. Losing it orphans every thread on it.
 *   - `h`    the content hash the guard compares (`ifHash`).
 *   - `rev`  the revision the block was last written at.
 * They ride through untouched. The server recomputes `h` and `rev` on write;
 * they are carried so that what we send back is what we were given, minus the
 * one thing the person actually changed.
 *
 * ==================================================================
 * THE CHIPS ARE ATOMIC, AND THAT IS THE POINT
 * ==================================================================
 * `mention`, `date`, `dropdown` and `embed` are inline leaf nodes with no
 * editable content. A person editing a sentence around an @-mention must be
 * able to delete it, move the cursor past it and type either side of it --
 * but not to open it up and rewrite half of it into something that is no
 * longer a mention of anybody. Atomic gives exactly that: one character wide
 * to the cursor, deletable as a unit, opaque inside.
 *
 * The four are declared with `toDOM` producing the same markup the read view
 * gives them (`Inline.svelte`), so a chip looks the same whether you are
 * editing the block or reading it.
 */
import { Schema, type NodeSpec, type MarkSpec } from 'prosemirror-model'

/** Attributes every block in this system carries, and must keep. */
const BLOCK_ATTRS = {
  id: { default: null as string | null },
  h: { default: null as string | null },
  rev: { default: null as number | null },
}

/** One chip: inline, atomic, opaque, and rendered as the read view renders it. */
function chip(kind: string, extra: Record<string, { default: unknown }> = {}): NodeSpec {
  return {
    inline: true,
    group: 'inline',
    atom: true,
    selectable: true,
    attrs: { id: { default: null }, ...extra },
    toDOM: (node) => {
      const a = node.attrs as Record<string, unknown>
      const label =
        kind === 'mention'
          ? `@${a.label ?? 'you'}`
          : kind === 'date'
            ? String(a.value ?? '')
            : kind === 'dropdown'
              ? String(a.label ?? '—')
              : 'chart'
      return ['span', { class: kind === 'mention' ? 'chip mention' : 'chip' }, label]
    },
  }
}

const nodes: Record<string, NodeSpec> = {
  /*
   * The whole tab.
   *
   * It was `content: 'block'` -- exactly one -- when the editor opened one
   * paragraph at a time in a little form. Euan, having used it: "it's not
   * really what I want the end product to be like. I want to edit the entire
   * document without it being in a form, much like how Google Docs work and
   * how the web-based Claude.ai artifacts work."
   *
   * So the schema has to describe every block a document can hold, not just
   * the two that are a single run of text. What a person may EDIT is still
   * narrower than what the schema can represent -- a table renders here and
   * is not yet typed into -- but anything the schema cannot represent would
   * be silently dropped the first time somebody typed in the paragraph above
   * it, and that is not a trade worth making for any of them.
   */
  doc: { content: 'block+' },

  paragraph: {
    group: 'block',
    content: 'inline*',
    attrs: {
      ...BLOCK_ATTRS,
      // 1-6, or null for body text. Our documents say "a paragraph that is a
      // heading" rather than having a separate heading node, and the renderer
      // reads the same attribute.
      heading: { default: null as number | null },
    },
    /*
     * The same `data-` attributes the read view puts on a block.
     *
     * `lib/selection.ts` maps a browser selection to "block id + character
     * offsets" by finding the nearest `[data-text-block]` and walking its
     * text. Carrying the attributes here means selecting a phrase and
     * commenting on it works the same while the document is editable as it
     * does while it is being read -- one implementation, not two.
     */
    toDOM: (node) => {
      const level = node.attrs.heading as number | null
      const id = node.attrs.id as string | null
      const attrs = id ? { 'data-id': id, 'data-text-block': id } : {}
      return [level && level >= 1 && level <= 6 ? `h${level}` : 'p', attrs, 0]
    },
  },

  codeBlock: {
    group: 'block',
    content: 'text*',
    marks: '',
    code: true,
    // Whitespace is the content, not formatting to be tidied away.
    whitespace: 'pre',
    attrs: { ...BLOCK_ATTRS, language: { default: null as string | null } },
    toDOM: (node) => {
      const id = node.attrs.id as string | null
      return ['pre', id ? { 'data-id': id, 'data-text-block': id } : {}, ['code', 0]]
    },
  },

  blockquote: {
    group: 'block',
    content: 'block+',
    attrs: { ...BLOCK_ATTRS },
    toDOM: () => ['blockquote', 0],
  },

  /*
   * One list node with a `kind` attribute, not three node types.
   *
   * That is how the documents are stored -- `{type:'list', attrs:{kind}}` with
   * 'bullet', 'ordered' or 'check' -- and the schema follows the JSON rather
   * than translating it, for the same reason the marks are `bold` and
   * `italic` instead of ProseMirror's usual `strong` and `em`: a translation
   * layer is a place for the two to drift.
   */
  list: {
    group: 'block',
    content: 'listItem+',
    attrs: { ...BLOCK_ATTRS, kind: { default: 'bullet' } },
    toDOM: (node) => [node.attrs.kind === 'ordered' ? 'ol' : 'ul', { class: `list-${node.attrs.kind}` }, 0],
  },

  listItem: {
    content: 'block+',
    defining: true,
    // `checked` only means anything in a check list; it rides on the item
    // because that is where the document puts it.
    attrs: { ...BLOCK_ATTRS, checked: { default: null as boolean | null } },
    toDOM: (node) => [
      'li',
      node.attrs.checked === null ? {} : { 'data-checked': node.attrs.checked ? 'true' : 'false' },
      0,
    ],
  },

  horizontalRule: {
    group: 'block',
    attrs: { ...BLOCK_ATTRS },
    toDOM: () => ['hr'],
  },

  /*
   * Tables, and the three nodes they are made of. Present so a document that
   * has one survives being edited around; `tableRole` is not set and no table
   * commands are bound, so there is nothing here that edits one yet.
   */
  table: {
    group: 'block',
    content: 'row+',
    attrs: { ...BLOCK_ATTRS },
    toDOM: () => ['table', ['tbody', 0]],
  },
  row: {
    content: 'cell+',
    attrs: { ...BLOCK_ATTRS },
    toDOM: () => ['tr', 0],
  },
  cell: {
    content: 'block+',
    attrs: { ...BLOCK_ATTRS, header: { default: null as boolean | null } },
    toDOM: (node) => [node.attrs.header ? 'th' : 'td', 0],
  },

  /*
   * A pending block is the agent's promise to write something here. It is not
   * a person's to type into -- it belongs to the thing that made it -- so it
   * is an ATOM: visible, selectable, deletable as a unit, opaque inside.
   */
  pending: {
    group: 'block',
    atom: true,
    selectable: true,
    attrs: { ...BLOCK_ATTRS, intent: { default: null as string | null } },
    toDOM: (node) => [
      'div',
      { class: 'pending', 'data-pending': '' },
      String(node.attrs.intent ?? 'Waiting for the agent…'),
    ],
  },

  /* Written by the system, never by a person: atoms, so they round-trip. */
  widget: {
    group: 'block',
    atom: true,
    selectable: true,
    attrs: { ...BLOCK_ATTRS, kind: { default: null }, data: { default: null } },
    toDOM: () => ['div', { class: 'widget' }, 'widget'],
  },
  upload: {
    group: 'block',
    atom: true,
    selectable: true,
    attrs: { ...BLOCK_ATTRS, name: { default: null }, href: { default: null } },
    toDOM: (node) => ['div', { class: 'upload' }, String(node.attrs.name ?? 'upload')],
  },

  text: { group: 'inline' },

  mention: chip('mention', { label: { default: null }, userId: { default: null } }),
  date: chip('date', { value: { default: null } }),
  dropdown: chip('dropdown', { label: { default: null }, value: { default: null } }),
  embed: chip('embed', { kind: { default: null }, data: { default: null } }),
}

/*
 * The marks, named exactly as the stored JSON names them.
 *
 * `bold` and `italic`, not ProseMirror's usual `strong` and `em`: the name in
 * the JSON is the name in the schema, because the JSON is the source of truth
 * and a translation layer is a place for the two to drift apart.
 */
const marks: Record<string, MarkSpec> = {
  bold: { toDOM: () => ['strong', 0] },
  italic: { toDOM: () => ['em', 0] },
  /*
   * `<s>`, not `<del>` -- the same reasoning the read view carries: `<del>` is
   * an edit record with a `cite` and a `datetime` saying who removed what, and
   * nothing here knows that. A struck run in these documents means "no longer
   * accurate", which is what `<s>` is for.
   */
  strike: { toDOM: () => ['s', 0] },
  /*
   * Underline has NO Markdown. Every other mark here survives an export as
   * `**`, `*`, `~~`, `` ` `` or `[]()`; this one can only be `<u>`, which is
   * HTML inside the Markdown. Euan asked for it, so it exists -- and the
   * serialiser writes `<u>…</u>`, which round-trips through this editor and
   * through any Markdown renderer that permits inline HTML, and degrades to
   * visible tags in one that does not. That trade is recorded in TASK-050.
   */
  underline: { toDOM: () => ['u', 0] },
  code: { toDOM: () => ['code', 0], excludes: '_' },
  link: {
    attrs: { href: { default: '' } },
    inclusive: false,
    toDOM: (mark) => ['a', { href: String(mark.attrs.href ?? ''), rel: 'noopener noreferrer' }, 0],
  },
}

export const blockSchema = new Schema({ nodes, marks })

/** The mark names a person can toggle, in the order the toolbar shows them. */
export const TOGGLEABLE = ['bold', 'italic', 'underline', 'strike', 'code'] as const
export type Toggleable = (typeof TOGGLEABLE)[number]

/**
 * A ProseMirror node as the DOCUMENT stores it.
 *
 * ProseMirror emits the whole `attrs` object whenever any attribute is set, so
 * a body cell comes back carrying `header: null` and a plain paragraph carries
 * `heading: null` -- defaults the schema had to declare, which the stored JSON
 * never had. Semantically harmless, since every reader treats them as falsy,
 * but it would mean each save quietly fattened the document with keys that
 * were not there before and that the agent's own writes do not produce.
 *
 * Null and undefined are dropped. `false`, `0` and `''` are KEPT: an unticked
 * checkbox is `checked: false` and means something quite different from a list
 * item with no checkbox at all.
 */
export function toStored<T>(value: T): T {
  if (Array.isArray(value)) return value.map(toStored) as unknown as T
  if (!value || typeof value !== 'object') return value
  const out: Record<string, unknown> = {}
  for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
    if (v === null || v === undefined) continue
    out[key] = toStored(v)
  }
  return out as T
}
